// Оркестрация движкового разбора поверх @ext/core/review.reviewGame.
// Мобильные ручки (PLAN-V2 Layer 2): quick pass depth ≤12 movetime 1200мс,
// deep pass только на ключевых моментах (Δ≥90cp) на depth 14, Hash 32 (в лоадере).
// Приоритет: позиция, которую юзер смотрит сейчас, считается СЛЕДУЮЩЕЙ —
// в обход очереди quick-прохода. Движок инжектится (DI) → юнит-тест на моке.
import { reviewGame, type AnalyzeFn, type ReviewProgress } from '@ext/core/review';
import type { EvalStore } from '@ext/core/evals';
import type { GameNode } from '@ext/core/types';
import type { EngineAnalysis } from '@ext/core/eval-utils';
import type { AnalyzeOptions } from '@ext/engine/engine';

export const ENGINE_DEPTH = 14; // quick=min(12) внутри reviewGame, deep=14 на key moments
export const KEY_MOMENT_CP = 90; // порог «ключевого момента» из PLAN-V2
const MOVETIME_QUICK_MS = 1200; // мобильная ручка quick-прохода
const MOVETIME_DEEP_MS = 3000; // потолок раздумий deep/on-demand (≤3с)
export const ON_DEMAND: AnalyzeOptions = { depth: ENGINE_DEPTH, multipv: 2, movetimeMs: MOVETIME_DEEP_MS };

/** Низкоуровневый анализ (engine-loader.analyze или мок/системный stockfish в тестах). */
export type LowLevelAnalyze = (fen: string, opts: AnalyzeOptions) => Promise<EngineAnalysis>;

interface Job {
  fen: string;
  opts: AnalyzeOptions;
  resolve: (a: EngineAnalysis) => void;
  reject: (e: unknown) => void;
}

/** Мобильные ручки для позиции quick/deep-прохода reviewGame. */
function mobileOpts(opts: { depth: number; multipv: number; movetimeMs?: number }): AnalyzeOptions {
  return {
    depth: opts.depth,
    multipv: opts.multipv,
    movetimeMs: opts.movetimeMs ? Math.min(opts.movetimeMs, MOVETIME_QUICK_MS) : MOVETIME_DEEP_MS,
  };
}

/**
 * Планировщик с приоритетом поверх одного движка. FIFO-очередь позиций
 * quick-прохода (`review`) + один приоритетный слот (`requestPriority`).
 * Насос берёт приоритет вперёд очереди, но НЕ прерывает уже стартовавшую
 * позицию — она доигрывается, приоритет идёт следующим. Движок один: пока
 * идёт вызов, следующий не стартует (chainRunning). Новый priority вытесняет
 * прошлый (reject 'superseded') — важна только позиция, которую смотрят сейчас.
 */
export class PriorityScheduler {
  private queue: Job[] = [];
  private priority: Job | null = null;
  private running = false;

  constructor(private lowLevel: LowLevelAnalyze) {}

  /** AnalyzeFn для reviewGame (низкий приоритет, FIFO). */
  review: AnalyzeFn = (fen, opts) =>
    new Promise<EngineAnalysis>((resolve, reject) => {
      this.queue.push({ fen, opts: mobileOpts(opts), resolve, reject });
      this.pump();
    });

  /** On-demand анализ текущей позиции — вперёд очереди. Вытесняет прошлый запрос. */
  requestPriority(fen: string, opts: AnalyzeOptions = ON_DEMAND): Promise<EngineAnalysis> {
    if (this.priority) this.priority.reject(new Error('superseded'));
    return new Promise<EngineAnalysis>((resolve, reject) => {
      this.priority = { fen, opts, resolve, reject };
      this.pump();
    });
  }

  private pump(): void {
    if (this.running) return;
    const job = this.priority ?? this.queue.shift();
    if (!job) return;
    if (job === this.priority) this.priority = null;
    this.running = true;
    this.lowLevel(job.fen, job.opts).then(
      (a) => this.done(() => job.resolve(a)),
      (e) => this.done(() => job.reject(e)),
    );
  }

  private done(settle: () => void): void {
    this.running = false;
    settle();
    this.pump();
  }
}

export interface RunReviewOptions {
  onProgress?: (p: ReviewProgress) => void;
  isCurrent?: () => boolean;
}

/** Полный движковый разбор партии с мобильными ручками через планировщик. */
export async function runReview(
  sched: PriorityScheduler,
  root: GameNode,
  evals: EvalStore,
  opts: RunReviewOptions = {},
): Promise<void> {
  await reviewGame(root, {
    analyze: sched.review,
    evals,
    depth: ENGINE_DEPTH,
    keyMomentCp: KEY_MOMENT_CP,
    onProgress: opts.onProgress,
    isCurrent: opts.isCurrent,
  });
}
